import { supabase, RequestWithRelations, PropertyType } from '@cleanpro/common-ui';
import {
  RequestStatus,
  RequestUpsertInput,
  SupabaseRequestRecord,
} from './types';

const transformRequestRecord = (
  record: SupabaseRequestRecord
): RequestWithRelations => {

  return {
    id: record.id,
    customerId: record.customer_id,
    serviceId: record.service_id,
    propertyId: record.property_id ?? '',
    notes: record.notes ?? '',
    status: record.status,
    urgency: record.urgency,
    estimatedDuration: record.estimated_duration ?? undefined,
    accessInstructions: record.access_instructions ?? undefined,
    referral: record.referral ?? undefined,
    exclusiveBusinessId: record.exclusive_business_id ?? undefined,
    exclusiveUntil: record.exclusive_until ?? undefined,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    requestedDateTime: record.requested_date_time ?? undefined,
    customer: record.customers
      ? {
          id: record.customers.id,
          name: record.customers.name,
          email: record.customers.email ?? undefined,
          phone: record.customers.phone ?? undefined,
          address: record.customers.address ?? undefined,
        }
      : undefined,
    service: record.services
      ? {
          id: record.services.id,
          name: record.services.name,
          description: record.services.description ?? '',
          basePrice: record.services.base_price ?? 0,
          duration: 0,
          category: 'residential' as const,
          addOns: [],
          createdAt: record.created_at,
          updatedAt: record.updated_at,
        }
      : undefined,
    property: record.properties
      ? {
          id: record.properties.id,
          name: '',
          customerId: record.customer_id,
          propertyType: record.properties.property_type as PropertyType,
          addressLine1: record.properties.address_line_1,
          addressLine2: record.properties.address_line_2 ?? undefined,
          city: record.properties.city,
          state: record.properties.state,
          postalCode: record.properties.postal_code,
          squareFootage: undefined,
          bedrooms: 0,
          bathrooms: 0,
          floors: 1,
          accessInstructions: record.properties.access_instructions ?? undefined,
          specialInstructions: record.properties.special_instructions ?? undefined,
          hasPets: false,
          petDetails: undefined,
          preferredCleaningDay: undefined,
          requestedTimeSlot: undefined,
          isActive: true,
          createdAt: record.created_at,
          updatedAt: record.updated_at,
        }
      : undefined,
  };
};

const mapToRequestPayload = (input: Partial<RequestUpsertInput>) => {
  const payload: Record<string, unknown> = {};

  if ('customerId' in input) payload.customer_id = input.customerId ?? null;
  if ('serviceId' in input) payload.service_id = input.serviceId ?? null;
  if ('propertyId' in input) payload.property_id = input.propertyId ?? null;
  if ('requestedDateTime' in input) payload.requested_date_time = input.requestedDateTime ?? null;
  if ('notes' in input) payload.notes = input.notes ?? null;
  if ('status' in input) payload.status = input.status;
  if ('urgency' in input) payload.urgency = input.urgency;
  if ('estimatedDuration' in input) payload.estimated_duration = input.estimatedDuration ?? null;
  if ('accessInstructions' in input) payload.access_instructions = input.accessInstructions ?? null;
  if ('referral' in input) payload.referral = input.referral ?? null;
  if ('exclusiveBusinessId' in input) payload.exclusive_business_id = input.exclusiveBusinessId ?? null;
  if ('exclusiveUntil' in input) payload.exclusive_until = input.exclusiveUntil ?? null;

  return payload;
};

export async function createRequest(input: RequestUpsertInput) {
  const payload = mapToRequestPayload(input);

  const { data, error } = await supabase
    .from('requests')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .insert(payload as any)
    .select(
      `
      id,
      customer_id,
      service_id,
      property_id,
      requested_date_time,
      notes,
      status,
      urgency,
      estimated_duration,
      referral,
      exclusive_business_id,
      exclusive_until,
      created_at,
      updated_at,
      customers (id, name, email, phone, address),
      services (id, name, description, base_price),
      properties (
        id,
        property_type,
        address_line_1,
        address_line_2,
        city,
        state,
        postal_code,
        access_instructions,
        special_instructions
      )
    `
    )
    .single();

  if (error) throw error;
  return transformRequestRecord(data as SupabaseRequestRecord);
}

export async function updateRequest(
  requestId: string,
  requestData: Partial<RequestUpsertInput>
) {
  const updates = {
    ...mapToRequestPayload(requestData),
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from('requests').update(updates).eq('id', requestId);
  if (error) throw error;
}

export async function updateRequestStatus(
  requestId: string,
  status: RequestStatus
) {
  const { error } = await supabase
    .from('requests')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', requestId);
  if (error) throw error;
}

export async function deleteRequest(requestId: string) {
  const { error } = await supabase.from('requests').delete().eq('id', requestId);
  if (error) throw error;
}

export async function fetchRequestById(
  requestId: string
): Promise<RequestWithRelations | null> {
  const { data, error } = await supabase
    .from('requests')
    .select(
      `
      id,
      customer_id,
      service_id,
      property_id,
      requested_date_time,
      notes,
      status,
      urgency,
      estimated_duration,
      referral,
      exclusive_business_id,
      exclusive_until,
      created_at,
      updated_at,
      customers (id, name, email, phone, address),
      services (id, name, description, base_price),
      properties (
        id,
        property_type,
        address_line_1,
        address_line_2,
        city,
        state,
        postal_code,
        access_instructions,
        special_instructions
      )
    `
    )
    .eq('id', requestId)
    .single();

  if (error) throw error;
  return data ? transformRequestRecord(data as SupabaseRequestRecord) : null;
}

export async function fetchAllRequests() {
  const { data, error } = await supabase
    .from('requests')
    .select(
      `
      id,
      customer_id,
      service_id,
      property_id,
      requested_date_time,
      notes,
      status,
      urgency,
      estimated_duration,
      referral,
      exclusive_business_id,
      exclusive_until,
      created_at,
      updated_at,
      customers (id, name, email, phone),
      services (id, name, description, base_price),
      properties (
        id,
        property_type,
        address_line_1,
        address_line_2,
        city,
        state,
        postal_code,
        access_instructions,
        special_instructions
      )
    `
    )
    .order('created_at', { ascending: false });

  if (error) {
    console.error('❌ Supabase error:', error);
    throw error;
  }

  return data.map((item) => transformRequestRecord(item as SupabaseRequestRecord));
}
